import React, { useRef, useState, useEffect, useCallback } from "react";
import Webcam from "react-webcam";
import axios from "axios";
import { baseURL } from "../../Config";
import styled, { keyframes } from "styled-components";
import { FaSpinner } from "react-icons/fa"; // Importing the spinner icon
import { toast } from "react-toastify";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  position: relative;
`;

const CaptureButton = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #007bff;
  border: none;
  border-radius: 5px;
  color: white;
  cursor: pointer;
  &:hover {
    background-color: #0056b3;
  }
`;

const LogContainer = styled.div`
  margin-top: 20px;
  background-color: #f7f7f7;
  padding: 15px;
  border-radius: 5px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(
    169,
    169,
    169,
    0.7
  ); /* Grey overlay with 70% opacity */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999; /* Ensure the overlay is on top of everything */
`;

const spinAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LargeLoadingIcon = styled(FaSpinner)`
  animation: ${spinAnimation} 2s linear infinite;
  font-size: 4em; /* Adjust the font size to make it large */
  color: red;
`;

const WebcamCapture = () => {
  const webcamRef = useRef(null);
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false); // state for loading
  const [debouncing, setDebouncing] = useState(false); // state for debouncing

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    uploadImage(imageSrc);
  }, [webcamRef]);

  const uploadImage = useCallback((base64Image) => {
    setLoading(true); // Set loading to true when starting the API call
    base64Image = base64Image.replace(
      /^data:image\/(png|jpg|jpeg);base64,/,
      ""
    );
    const apiUrl = baseURL + "ocr/get";

    axios
      .post(
        apiUrl,
        {
          image: base64Image,
        },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      )
      .then((response) => {
        setLog(response.data.data);
        toast.success(response?.data?.messages);
        setLoading(false); // Set loading to false when API call is complete
      })
      .catch((error) => {
        console.error("Error uploading image:", error);
        toast.error("Error uploading image");
        setLoading(false); // Set loading to false when an error occurs
      });
  }, []);

  const debounceCapture = useCallback(() => {
    if (!debouncing) {
      capture();
      setDebouncing(true);
    }
  }, [capture, debouncing]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === "Space" && !debouncing) {
        debounceCapture();
      }
    };

    const handleKeyUp = (event) => {
      if (event.code === "Space") {
        setDebouncing(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [debounceCapture, debouncing]);

  return (
    <Container>
      <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" />
      <CaptureButton onMouseDown={debounceCapture}>
        Capture & Upload
      </CaptureButton>
      {loading && (
        <LoadingOverlay>
          <LargeLoadingIcon />
        </LoadingOverlay>
      )}
    </Container>
  );
};

export default WebcamCapture;
